import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilm } from '@fortawesome/pro-light-svg-icons/faFilm';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { canDownloadProRes } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../../common/types';
import { useGetKitProResQuery, useRequestKitProResMutation } from '../../../../redux/api/fymAPI';
import { useStudioPlan } from '../studio/useStudioPlan';
import { AssetViewerItem } from './viewerItems';
import { viewerMessages } from './viewerMessages';
import { proResView } from './proResView';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the viewer; each part uses its own slice of it */
import styles from './AssetViewer.module.scss';
import { trackAssetDownloaded } from '../assetEvents';
/* eslint-enable css-modules/no-unused-class */

interface ViewerProResProps {
  accountId: number;
  catalogItemUuid: string;
  item: AssetViewerItem;
  isDemo: boolean;
}

/** While the ProRes file is made, the page reads the state this often (the socket also says when it is done). */
const WORKING_POLL_MS = 10_000;

/**
 * "Download ProRes for distributor" on a motion artwork file (design w, owner decision 2026-10-08):
 * the server makes a ProRes 422 HQ MOV on request and keeps it 7 days; it is never in the kit zip.
 * Pro or higher only (`canDownloadProRes`): other plans see the button locked with "ProRes for
 * distributors is on Pro".
 */
const ViewerProResComponent: React.FC<ViewerProResProps> = ({ accountId, catalogItemUuid, item, isDemo }) => {
  const intl = useIntl();
  const { plan } = useStudioPlan(accountId);
  const unlocked = plan !== undefined && canDownloadProRes(plan);
  const args = { accountId, catalogItemUuid, assetUuid: item.assetUuid };
  const [pollingInterval, setPollingInterval] = useState(0);
  const { data } = useGetKitProResQuery(args, { skip: !unlocked || isDemo, pollingInterval });
  const [request, { isLoading }] = useRequestKitProResMutation();
  const view = proResView({ unlocked, status: data?.status ?? 'none', requesting: isLoading });
  const working = view.kind === 'working';
  useEffect(() => setPollingInterval(working ? WORKING_POLL_MS : 0), [working]);
  const make = useCallback(() => {
    request({ accountId, catalogItemUuid, assetUuid: item.assetUuid }).catch(() => undefined);
  }, [accountId, catalogItemUuid, item.assetUuid, request]);

  if (view.kind === 'locked') {
    return (
      <div className={styles.acts} data-testid="viewer-prores">
        <button type="button" className={clsx(styles.btn, styles.full)} disabled data-testid="viewer-prores-locked">
          <FontAwesomeIcon icon={faLock} />
          {intl.formatMessage(viewerMessages.proResDownload)}
        </button>
        <div className={clsx(styles.small, styles.full)}>{intl.formatMessage(viewerMessages.proResLocked)}</div>
      </div>
    );
  }
  const ready = data?.status === 'ready' ? data : null;
  return (
    <div className={styles.acts} data-testid="viewer-prores">
      {ready !== null ? (
        <a
          className={clsx(styles.btn, styles.full)}
          href={ready.url}
          download={ready.fileName}
          data-testid="viewer-prores-download"
          onClick={() => trackAssetDownloaded(item.themeId, 'viewer')}
        >
          <FontAwesomeIcon icon={faFilm} />
          {intl.formatMessage(viewerMessages.proResDownload)}
        </a>
      ) : (
        <button
          type="button"
          className={clsx(styles.btn, styles.full)}
          disabled={isDemo || working}
          onClick={make}
          data-testid="viewer-prores-make"
        >
          <FontAwesomeIcon icon={faFilm} />
          {intl.formatMessage(working ? viewerMessages.proResWorking : viewerMessages.proResMake)}
        </button>
      )}
      <div className={clsx(styles.small, styles.full)}>
        {ready !== null
          ? intl.formatMessage(viewerMessages.proResReady, {
              date: intl.formatDate(ready.expiresAt, { day: 'numeric', month: 'short' }),
            })
          : intl.formatMessage(viewerMessages.proResHint)}
      </div>
      {view.kind === 'failed' && (
        <div className={clsx(styles.error, styles.full)} role="alert">
          {intl.formatMessage(viewerMessages.proResFailed)}
        </div>
      )}
    </div>
  );
};

export const ViewerProRes = TypedMemo(ViewerProResComponent);
